import { ViewerFragmentCommonProps } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useMediaById } from "@coworker/app/src/components/RoomSettings/hooks/useMediaById";
import { useTranslation } from "@coworker/locales";
import LoadingViewerFragment from "../LoadingViewerFragment/LoadingViewerFragment";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { MediaGroupedListView } from "@coworker/apprestructured/src/shared/components/MediaGroupedListView/MediaGroupedListView";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { ItemNoAndTypeWithPieces } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import styles from "../MediaLocationsViewerFragment/MediaLocationsViewerFragment.module.css";
import { useGroupsByMediaId } from "@coworker/app/src/components/RoomSettings/hooks/useGroupsByMediaId";
import { groupMediasByType } from "@coworker/apprestructured/src/task-management/helpers/media.helper";

export type MediaLocationViewerFragmentConfigProps = {
  field: string;
  title: string;
};

type MediaLocationViewerFragmentProps = {
  configProps: MediaLocationViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

function MediaLocationViewerFragment({
  configProps,
  commonProps,
}: MediaLocationViewerFragmentProps) {
  const { field, title } = configProps;
  const { task } = commonProps;
  const mediaId = getTypedValue<string>(getByPath(task, field));
  const rawGroupId = getTypedValue<string>(getByPath(task, "details.group_id"));
  const groupId = typeof rawGroupId === "string" ? rawGroupId : undefined;
  const items = getTypedValue<
    (ItemNoAndTypeWithPieces & { article_id?: string })[]
  >(getByPath(task, "details.items"));
  const hasMediaId = typeof mediaId === "string" && mediaId.length > 0;
  const { data: mediaResponse, isLoading: isMediaLoading } = useMediaById(
    hasMediaId ? mediaId : null
  );
  const { data: groups } = useGroupsByMediaId(hasMediaId ? mediaId : undefined);
  const { t } = useTranslation();
  const navigate = useFixaNavigate();

  if (isLoading(mediaId)) {
    return <LoadingViewerFragment />;
  }

  if (!hasMediaId) {
    // ponytail: task can't be created with an empty media_id, so empty here means deleted
    return (
      <ModalColumn gap="small">
        <FixaText textStyle="Heading.S" tagName="h2">
          {t(title)}
        </FixaText>
        <FixaInlineMessage
          variant="negative"
          title={t("mediaDeletedString")}
          body={t("mediaDeletedInTaskDescriptionString")}
        />
      </ModalColumn>
    );
  }

  if (isMediaLoading) {
    return <LoadingViewerFragment />;
  }

  const mediaName = mediaResponse?.media?.name?.trim() ?? "";
  const media = mediaResponse?.media;
  if (mediaName.length === 0 || !media) {
    return null;
  }

  const nbrArticles = Array.isArray(items)
    ? items.reduce((count, item) => count + item.pieces, 0)
    : media.nbrArticles;
  const mediaType = (media.mediaType ?? MediaTypes.NONE) as MediaTypes;
  const groupName = groups?.find((group) => group.id === groupId)?.name ?? "";
  const articleId = Array.isArray(items)
    ? (items.find((item) => item.article_id)?.article_id ?? mediaId)
    : mediaId;
  const medias: MediasAndArticleCountResponse[] = [
    {
      name: media.name,
      nbrArticles,
      articleId,
      groupId: groupId ?? "",
      groupName,
      mediaId: media.id,
      mediaType,
      parentMediaName: mediaResponse?.parent?.name ?? "",
      area: media.areaHfb,
    },
  ];
  const groupedMedias = groupMediasByType(medias);

  return (
    <ModalColumn gap="small">
      <FixaText textStyle="Heading.S" tagName="h2">
        {t(title)}
      </FixaText>
      <MediaGroupedListView
        groupedMediasByType={groupedMedias}
        showChevron={true}
        showQuantityLabel={false}
        onItemClick={(media) =>
          navigate(`/mediamaintenance/tabs/${media.mediaId}`)
        }
        itemClassName={styles["mediaItem"]}
        headerClassName={styles["mediaTypeHeader"]}
        additionalProps={{ inset: true }}
      />
    </ModalColumn>
  );
}

export default MediaLocationViewerFragment;
